import type { Language } from '@/utils/i18n/language.interface';
import { createFeatureSelector, createSelector, type MemoizedSelector } from '@ngrx/store';
import type { UserCore, UserId } from '../../interfaces/User.interface';
import type { AppState } from '../app.state';
import type { State } from './profile.state';

const getIsPending = (state: State): boolean => state.isPending;
const getIsCurrentUserLoaded = (state: State): boolean => state.isCurrentUserLoaded;
const getCurrentUser = (state: State): UserCore | null => state.currentUser;
const getRights = (state: State): string[] => state.rights;
const getAreRightsLoaded = (state: State): boolean => state.areRightsLoaded;

const getUserId = (user: UserCore | null): UserId | null => user?.id ?? null;
const getUserName = (user: UserCore | null): string | null => {
    if (!user) {
        return null;
    }
    if (user.firstname && user.lastname) return `${user.firstname} ${user.lastname}`;
    else if (user.firstname && !user.lastname) return user.firstname;
    else if (!user.firstname && user.lastname) return user.lastname;
    else return user.email;
};
const getUserEmail = (user: UserCore | null): string | null => user?.email ?? null;
const getUserPictureUrl = (user: UserCore | null): string | null => user?.pictureUrl ?? null;
const getUserLanguage = (user: UserCore | null): Language | null => user?.language ?? null;

export const selectProfileState: MemoizedSelector<AppState, State> = createFeatureSelector('profile');

export const selectIsPending: MemoizedSelector<AppState, boolean> = createSelector(selectProfileState, getIsPending);

export const selectIsCurrentUserLoaded: MemoizedSelector<AppState, boolean> = createSelector(
    selectProfileState,
    getIsCurrentUserLoaded
);

export const selectCurrentUser: MemoizedSelector<AppState, UserCore | null> = createSelector(
    selectProfileState,
    getCurrentUser
);

export const selectRights: MemoizedSelector<AppState, string[]> = createSelector(selectProfileState, getRights);

export const selectAreRightsLoaded: MemoizedSelector<AppState, boolean> = createSelector(
    selectProfileState,
    getAreRightsLoaded
);

export const selectCurrentUserId: MemoizedSelector<AppState, UserId | null> = createSelector(
    selectCurrentUser,
    getUserId
);

export const selectCurrentUserName: MemoizedSelector<AppState, string | null> = createSelector(
    selectCurrentUser,
    getUserName
);

export const selectCurrentUserEmail: MemoizedSelector<AppState, string | null> = createSelector(
    selectCurrentUser,
    getUserEmail
);

export const selectCurrentUserPictureUrl: MemoizedSelector<AppState, string | null> = createSelector(
    selectCurrentUser,
    getUserPictureUrl
);

export const selectCurrentUserLanguage: MemoizedSelector<AppState, Language | null> = createSelector(
    selectCurrentUser,
    getUserLanguage
);
